import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DollarSign, TrendingUp, AlertTriangle, Loader2, Printer, HelpCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOOLTIP = "Platform fees earned. Access controlled in Owner Tools → Permissions.";

export default function PlatformRevenueTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 90); return d.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (fromDate) params.set("from_date", `${fromDate}T00:00:00Z`);
    if (toDate)   params.set("to_date",   `${toDate}T23:59:59Z`);
    axios.get(`${API}/admin/platform-revenue?${params.toString()}`)
      .then((res) => setData(res.data))
      .catch((e) => setError(e.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  }, [fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const handlePrint = () => window.print();

  if (loading && !data) return <div className="flex items-center gap-2 text-slate-500 p-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  if (error) return <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{error}</div>;
  if (!data) return null;

  return (
    <div className="space-y-6 platform-revenue-print-root" data-testid="platform-revenue-tab">
      <style>{`
        @media print {
          @page { margin: 12mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .platform-revenue-print-root > * { break-inside: avoid; page-break-inside: avoid; }
          .platform-revenue-print-root table thead { display: table-header-group; }
          .platform-revenue-print-root table tr { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>

      {/* Toolbar: title with tooltip, date range, print */}
      <div className="flex items-center justify-between flex-wrap gap-3 print:hidden">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-600" /> Platform Revenue
          </h2>
          <span
            title={TOOLTIP}
            className="text-slate-400 hover:text-slate-600 cursor-help"
            data-testid="platform-revenue-tooltip"
          >
            <HelpCircle className="w-4 h-4" />
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <label className="text-xs text-slate-500">From</label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-9 w-40" data-testid="platform-revenue-from-date" />
          </div>
          <div className="flex items-center gap-1">
            <label className="text-xs text-slate-500">To</label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-9 w-40" data-testid="platform-revenue-to-date" />
          </div>
          <Button variant="outline" size="sm" onClick={handlePrint} data-testid="platform-revenue-print-btn">
            <Printer className="w-4 h-4 mr-1" /> Print
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {[
          { label: "Month to Date", ...data.monthToDate },
          { label: "Year to Date", ...data.yearToDate },
          { label: "All Time", ...data.allTime },
        ].map((row) => (
          <Card key={row.label} className="border-emerald-100">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-500 font-normal">{row.label}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-emerald-700">${row.revenue.toFixed(2)}</p>
              <p className="text-xs text-slate-400 mt-1">{row.txns} transaction(s)</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {data.subscriptionRevenue && (
        <Card className="border-teal-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-500 font-normal">Center Subscription Revenue (MTD)</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-teal-700">${data.subscriptionRevenue.mtd.toFixed(2)}</p>
            <p className="text-xs text-slate-400 mt-1">{data.subscriptionRevenue.activeCenters} active center(s)</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-800">
            <TrendingUp className="w-5 h-5" /> Revenue by Center
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.byCenter.length === 0 ? (
            <p className="text-sm text-slate-400">No centers with paid transactions yet.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {data.byCenter.map((c) => (
                <div key={c.organizationId} className="py-2 flex items-center justify-between" data-testid={`revenue-center-${c.organizationId}`}>
                  <div>
                    <p className="font-medium text-slate-800">{c.name}</p>
                    <p className="text-xs text-slate-400">{c.txns} transactions</p>
                  </div>
                  <p className="font-semibold text-emerald-700">${c.revenue.toFixed(2)}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Individual transactions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-800">
            <DollarSign className="w-5 h-5" /> Transactions
            <span className="text-xs font-normal text-slate-400">
              {new Date(data.dateRange.from).toLocaleDateString()} – {new Date(data.dateRange.to).toLocaleDateString()}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.transactions?.length === 0 ? (
            <p className="text-sm text-slate-400">No transactions in this date range.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-4 font-medium">Date</th>
                    <th className="py-2 pr-4 font-medium">Center</th>
                    <th className="py-2 pr-4 font-medium">Payment ID</th>
                    <th className="py-2 pr-4 font-medium text-right">Platform Fee</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.transactions.map((t, i) => (
                    <tr key={i} data-testid={`platform-txn-row-${i}`}>
                      <td className="py-2 pr-4 text-slate-700">{new Date(t.timestamp).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-slate-700">{t.centerName}</td>
                      <td className="py-2 pr-4 text-slate-500 text-xs font-mono">{t.paymentId || "—"}</td>
                      <td className="py-2 pr-4 text-emerald-700 font-semibold text-right">${t.fee.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-amber-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="w-5 h-5" /> Failed Payments (last 30 days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.recentFailures.length === 0 ? (
            <p className="text-sm text-slate-400">No failed payments in the last 30 days.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {data.recentFailures.map((f, i) => (
                <div key={i} className="py-2 text-sm">
                  <p className="font-medium text-slate-800">{f.centerName}</p>
                  <p className="text-xs text-slate-400">{new Date(f.timestamp).toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
